// TanStack Query hooks for transform_versions.
// Mirrors useAgentFileVersions — restore reuses useSaveTransform.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { TransformVersion } from "@/types/ontology";
import { TRANSFORMS_KEY, useSaveTransform } from "@/hooks/useTransforms";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export function transformVersionsKey(transformId: string) {
  return ["transform-versions", transformId] as const;
}

// ── Query ─────────────────────────────────────────────────────────────────────

export function useTransformVersions(transformId: string) {
  return useQuery({
    queryKey: transformVersionsKey(transformId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transform_versions")
        .select("*")
        .eq("transform_id", transformId)
        .order("version_number", { ascending: false });

      if (error) throw new Error(error.message);

      return (data as TransformVersion[]) ?? [];
    },
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface RestoreTransformVersionArgs {
  /** The live transform's id. */
  transformId: string;
  /** The version whose content we want to restore. */
  version: TransformVersion;
  /** The current_version loaded in the editor (for optimistic locking). */
  expectedVersion: number;
  /** Optional note; defaults to "Restored from v{n}". */
  note?: string;
}

export function useRestoreTransformVersion() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const saveTransform = useSaveTransform();

  return useMutation({
    mutationFn: async ({
      transformId,
      version,
      expectedVersion,
      note,
    }: RestoreTransformVersionArgs) => {
      // Load the current transform to get required fields for the save input.
      const { data: current, error } = await supabase
        .from("transforms")
        .select("*")
        .eq("id", transformId)
        .single();

      if (error || !current) throw new Error("Failed to load current transform");

      return saveTransform.mutateAsync({
        id: transformId,
        expectedVersion,
        input: {
          name: current.name,
          description: current.description,
          source_object_type_id: current.source_object_type_id,
          target_object_type_id: current.target_object_type_id,
          code: version.code,
          trigger_mode: current.trigger_mode,
        },
        note: note ?? `Restored from v${version.version_number}`,
      });
    },
    onSuccess: (_data, { transformId }) => {
      queryClient.invalidateQueries({ queryKey: transformVersionsKey(transformId) });
      queryClient.invalidateQueries({ queryKey: TRANSFORMS_KEY });
    },
    onError: (err) => {
      console.error("hook:useRestoreTransformVersion", err);
      toast({
        title: "Failed to restore transform version",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}
